/**
 * /el/guides/prothesmia-rsvp-gamou
 *
 * The Greek counterpart of `when-should-wedding-guests-rsvp.en.js` — the date
 * question, and the head of the Greek RSVP cluster.
 *
 * ── The one thing this article refuses to become ─────────────────────────
 * A large share of Greek searches around RSVP are guest-side: «τι σημαίνει
 * RSVP», «τι απαντάω». That is a real query and it is not this audience.
 * Agapaé writes for the couple, so the article stays couple-facing throughout
 * and the guest-side question is answered once, in a single FAQ line, rather
 * than being allowed to take over the piece. Do not let a later edit turn this
 * into a definition page; the traffic it would win is not traffic that plans a
 * wedding.
 *
 * ── The adaptation ───────────────────────────────────────────────────────
 * The method is the English article's, unchanged: the deadline is DERIVED by
 * working backward from the final-count date, not chosen off a calendar. Every
 * number stays hedged — «αφετηρία», «παραδείγματα και όχι κανόνες» — because a
 * κτήμα that wants numbers five weeks out is not wrong.
 *
 * What is Greek rather than translated:
 *
 *  - **Το κτήμα και το catering** are the vendors that set the real deadline,
 *    and «τελικός αριθμός ατόμων» is what they ask for on the phone.
 *  - **The destination section is not about destination weddings.** The Greek
 *    equivalent is the wedding on an island or in the village the family comes
 *    from, with guests flying or driving from Athens — which is common enough
 *    to be ordinary, not a category.
 *  - **Προσκλητήρια** are still often delivered by hand here, and that changes
 *    the send-date arithmetic, so it is said rather than assumed.
 *
 * The five milestones the English guide keeps deliberately apart — προθεσμία
 * RSVP, τελικός αριθμός για τους συνεργάτες, αποστολή προσκλητηρίων, περίοδος
 * υπενθυμίσεων, τελική λίστα — stay five. Collapsing any two is the mistake the
 * whole article exists to prevent.
 *
 * ── The claims this guide is allowed to make ─────────────────────────────
 * Four, all in the closing section:
 *
 *   - «Τα εργαλεία RSVP είναι διαθέσιμα στο πακέτο Heirloom.»
 *   - couples manage the guests they invite
 *   - couples review the responses
 *   - what follows in the planning uses that information
 *
 * And what it must never say, because none of it exists: automatic reminders,
 * automatic follow-ups, automatic closing of the guest count, automatic
 * seating, personalised RSVP emails, automatic messages to vendors, unlimited
 * RSVPs, live synchronisation with a supplier. The article gives reminder and
 * follow-up advice at length — that advice is addressed to the READER and is
 * never described as something the product does for them. Keep it that way.
 *
 * No copy in this file contains "&", "<" or ">".
 */

const whenShouldWeddingGuestsRsvpEl = {
  slug: "prothesmia-rsvp-gamou",

  title: "Προθεσμία RSVP γάμου: πότε να ζητήσετε τις απαντήσεις",
  metaTitle: "Προθεσμία RSVP γάμου: πότε να ζητήσετε τις απαντήσεις | Agapaé",
  metaDescription:
    "Πότε να βάλετε προθεσμία RSVP, πότε να στείλετε τα προσκλητήρια και πόσο χρόνο να αφήσετε για τις καθυστερημένες απαντήσεις και τον τελικό αριθμό ατόμων.",

  eyebrow: "RSVP γάμου",

  hero: {
    src: "/assets/images/moments/moment_3.jpg",
    alt: "Ασπρόμαυρη φωτογραφία νύφης και γαμπρού από πίσω, καθώς περπατούν χέρι χέρι μέσα στο χαμηλό απογευματινό φως, με το πέπλο και το νυφικό να φωτίζονται",
    width: 6558,
    height: 3659,
  },

  intro: [
    `Η προθεσμία για τις απαντήσεις μοιάζει λεπτομέρεια, μέχρι να καταλάβετε πόσα κρέμονται από αυτήν.`,
    `Πρέπει να ξέρετε πόσοι έρχονται για να κλείσετε τη λίστα σας, να δώσετε αριθμό στο κτήμα και στο catering, να δείτε τι χρειάζεται ο καθένας στο φαγητό και να φτιάξετε το πλάνο τραπεζιών.`,
    `Πότε λοιπόν πρέπει να απαντήσουν οι καλεσμένοι;`,
    `Στους περισσότερους γάμους μια χρήσιμη αφετηρία είναι τρεις με τέσσερις εβδομάδες πριν. Η καλύτερη προθεσμία όμως δεν είναι ένας αριθμός στο ημερολόγιο. Είναι η ημερομηνία που σας αφήνει χρόνο να κυνηγήσετε όσους δεν απάντησαν και να προλάβετε την προθεσμία που σας έχουν δώσει οι συνεργάτες σας.`,
    `Ο πιο σίγουρος τρόπος να τη βρείτε είναι να μετρήσετε ανάποδα.`,
  ],

  sections: [
    {
      heading: "Πότε να ζητήσετε τις απαντήσεις",
      content: [
        `Για έναν συνηθισμένο γάμο, βάλτε την προθεσμία γύρω στις τρεις με τέσσερις εβδομάδες πριν.`,
        `Έτσι μένει ένα χρήσιμο περιθώριο ανάμεσα στην ημερομηνία που ζητάτε από τους καλεσμένους και στην ημερομηνία που πρέπει να κλείσετε αριθμό με τον χώρο.`,
        `Αν, για παράδειγμα, το catering θέλει τον τελικό αριθμό δύο εβδομάδες πριν, μια προθεσμία τρεις με τέσσερις εβδομάδες νωρίτερα σάς δίνει χρόνο να:`,
        {
          type: "list",
          items: [
            `δείτε ποιοι δεν έχουν απαντήσει`,
            `επικοινωνήσετε μαζί τους`,
            `ξεκαθαρίσετε τις ασαφείς απαντήσεις`,
            `καταγράψετε τις αλλαγές`,
            `κλείσετε τη λίστα σας`,
            `ξεκινήσετε το πλάνο τραπεζιών`,
          ],
        },
        `Το διάστημα μπορεί να είναι μικρότερο ή μεγαλύτερο, ανάλογα με τους συνεργάτες σας και τους καλεσμένους σας.`,
        `Το σημαντικό είναι να μη διαλέξετε την ημερομηνία μεμονωμένα.`,
      ],
    },
    {
      heading: "Γιατί οι τρεις με τέσσερις εβδομάδες δουλεύουν",
      content: [
        `Το διάστημα αυτό αφήνει ένα πραγματικό κενό ανάμεσα στην προθεσμία των καλεσμένων και στις δικές σας προθεσμίες.`,
        `Αν η προθεσμία πέσει πολύ κοντά στον γάμο, δυο τρεις καθυστερημένες απαντήσεις αρκούν για να σας πιέσουν.`,
        `Και μπορεί να ψάχνετε ακόμη:`,
        {
          type: "list",
          items: [
            `πόσα πιάτα να παραγγείλετε`,
            `πόσα τραπέζια χρειάζεστε`,
            `ποιος κάθεται πού`,
            `ποιοι έρχονται τελικά`,
            `αν κάποιος άλλαξε τα σχέδιά του`,
          ],
        },
        `Από την άλλη, μια υπερβολικά πρώιμη προθεσμία δημιουργεί το αντίθετο πρόβλημα.`,
        `Οι καλεσμένοι μπορεί να μην ξέρουν ακόμη αν μπορούν, πώς θα έρθουν ή αν θα πάρουν άδεια. Μια βιαστική ερώτηση φέρνει βιαστικές απαντήσεις, που μετά αλλάζουν.`,
        `Μια καλή προθεσμία ισορροπεί δύο ανάγκες:`,
        `Οι καλεσμένοι χρειάζονται χρόνο να αποφασίσουν.`,
        `Εσείς χρειάζεστε χρόνο να δουλέψετε πάνω στις απαντήσεις τους.`,
      ],
    },
    {
      heading: "Μετρήστε ανάποδα, από το κτήμα και το catering",
      content: [
        `Πριν διαλέξετε ημερομηνία, ρωτήστε τον χώρο και το catering:`,
        `Πότε θέλετε τον τελικό αριθμό ατόμων;`,
        `Είναι η πιο χρήσιμη ερώτηση όλης της διαδικασίας.`,
        `Πολλές φορές η ημερομηνία υπάρχει ήδη γραμμένη στο συμφωνητικό σας, και αυτή υπερισχύει κάθε γενικού κανόνα.`,
        `Μόλις τη μάθετε, μετρήστε ανάποδα:`,
        {
          type: "steps",
          items: [
            `Η ημέρα του γάμου`,
            `Ο τελικός αριθμός για τον χώρο και το catering`,
            `Χρόνος για υπενθυμίσεις και διορθώσεις`,
            `Η προθεσμία RSVP`,
            `Χρόνος για να φτάσει το προσκλητήριο και να απαντήσουν`,
            `Η αποστολή των προσκλητηρίων`,
          ],
        },
        `Οι μέρες ανάμεσα σε αυτά τα βήματα αλλάζουν από γάμο σε γάμο.`,
        `Η λογική μένει η ίδια: ξεκινάτε από την προθεσμία που δεν μπορείτε να χάσετε και πάτε προς τα πίσω.`,
      ],
    },
    {
      heading: "Πότε να στείλετε τα προσκλητήρια",
      content: [
        `Σε πολλούς γάμους τα προσκλητήρια φεύγουν έξι με οκτώ εβδομάδες πριν.`,
        `Δεν είναι όμως κανόνας.`,
        `Στείλτε τα νωρίτερα όταν:`,
        {
          type: "list",
          items: [
            `πολλοί καλεσμένοι θα ταξιδέψουν`,
            `έρχεται κόσμος από το εξωτερικό`,
            `ο γάμος πέφτει κοντά σε αργίες ή μέσα στο καλοκαίρι`,
            `χρειάζεται να κλείσουν διαμονή`,
            `οι καλεσμένοι πρέπει να ζητήσουν άδεια από τη δουλειά`,
            `δεν έχετε στείλει save the date`,
            `ο γάμος γίνεται σε νησί ή σε άλλη πόλη για τους περισσότερους`,
          ],
        },
        `Υπολογίστε και τον τρόπο αποστολής. Στην Ελλάδα πολλά προσκλητήρια δίνονται στο χέρι, και αυτό θέλει χρόνο και ραντεβού — ενώ ένα ηλεκτρονικό προσκλητήριο φτάνει την ίδια μέρα.`,
        `Η ημερομηνία αποστολής και η προθεσμία απάντησης αποφασίζονται μαζί.`,
        `Δεν έχει νόημα να κλειδώσετε προθεσμία και μετά να ανακαλύψετε ότι οι καλεσμένοι έχουν δέκα μέρες για να λάβουν το προσκλητήριο και να απαντήσουν.`,
      ],
    },
    {
      heading: "Πόσο χρόνο χρειάζονται οι καλεσμένοι",
      content: [
        `Ο καθένας χρειάζεται αρκετό χρόνο για να δει αν μπορεί να έρθει και, όπου χρειάζεται, να κανονίσει το ταξίδι του.`,
        `Σε έναν γάμο όπου όλοι μένουν κοντά, το διάστημα μπορεί να είναι σύντομο.`,
        `Για όποιον ταξιδεύει, η απόφαση περιλαμβάνει:`,
        {
          type: "list",
          items: [
            `αεροπορικά ή ακτοπλοϊκά εισιτήρια`,
            `διαμονή`,
            `άδεια από τη δουλειά`,
            `με ποιον θα αφήσουν τα παιδιά`,
            `συνεννόηση με την υπόλοιπη οικογένεια`,
          ],
        },
        `Γι' αυτό δεν υπάρχει ένα χρονοδιάγραμμα που να ταιριάζει σε κάθε γάμο.`,
        `Σκεφτείτε τι χρειάζεται πραγματικά ένας μέσος καλεσμένος σας για να σας δώσει μια απάντηση που θα κρατήσει.`,
        `Όσο πιο σύνθετη είναι η μετακίνηση, τόσο πιο χρήσιμο είναι το περιθώριο.`,
      ],
    },
    {
      heading: "Γάμος σε νησί ή μακριά από την πόλη σας",
      content: [
        `Ο γάμος στο νησί ή στο χωριό της οικογένειας δεν είναι εξαίρεση στην Ελλάδα· είναι από τις πιο συνηθισμένες περιπτώσεις.`,
        `Και αλλάζει το χρονοδιάγραμμα, γιατί ο καλεσμένος δεν αποφασίζει μόνο αν θα έρθει: αποφασίζει και πώς θα φτάσει, πού θα μείνει και πόσες μέρες θα λείψει.`,
        `Πολλά ζευγάρια βάζουν εκεί την προθεσμία νωρίτερα από ό,τι σε έναν γάμο στην πόλη.`,
        `Μια αφετηρία είναι οι τέσσερις με έξι εβδομάδες πριν, με τα προσκλητήρια να φεύγουν αντίστοιχα νωρίτερα.`,
        `Ακόμη κι εκεί όμως, μετρήστε ανάποδα από τις δικές σας προθεσμίες.`,
        `Αν ο χώρος θέλει αριθμό νωρίτερα, η προθεσμία πάει νωρίτερα.`,
        `Και αν οι περισσότεροι έρχονται από μακριά, ο επιπλέον χρόνος κάνει τη ζωή όλων πιο εύκολη — και τα δωμάτια βρίσκονται όσο υπάρχουν ακόμη.`,
      ],
    },
    {
      heading: "Όταν πολλοί καλεσμένοι ταξιδεύουν",
      content: [
        `Δεν χρειάζεται να κάνετε γάμο σε νησί ή στο εξωτερικό για να παίξει ρόλο το ταξίδι στο χρονοδιάγραμμά σας.`,
        `Μπορεί να γίνεται στην πόλη σας και οι μισοί καλεσμένοι να μένουν αλλού.`,
        `Αν αρκετοί πρέπει να κλείσουν εισιτήρια, διαμονή ή άδεια, δώστε τους περισσότερο χρόνο.`,
        `Η ερώτηση είναι μία:`,
        `Πόσο χρόνο χρειάζεται ένας τυπικός καλεσμένος σας για να απαντήσει ρεαλιστικά;`,
        `Αν η απάντηση είναι «περισσότερο από όσο έχω αφήσει», αλλάξτε το χρονοδιάγραμμα.`,
        `Η προθεσμία υπάρχει για τη δική σας λίστα, όχι για έναν αφηρημένο κανόνα.`,
      ],
    },
    {
      heading: "Πότε να θυμίσετε",
      content: [
        `Μην περιμένετε να πλησιάσει ο γάμος για να ανακαλύψετε ότι λείπουν απαντήσεις.`,
        `Μια υπενθύμιση πριν από την προθεσμία βοηθά όσους:`,
        {
          type: "list",
          items: [
            `δεν είδαν το προσκλητήριο`,
            `το είχαν σκοπό και το ξέχασαν`,
            `περιμένουν να κλείσουν το ταξίδι τους`,
            `νόμιζαν ότι απάντησε κάποιος άλλος από το σπίτι`,
          ],
        },
        `Στην πράξη δουλεύει καλά μια φιλική υπενθύμιση περίπου μία εβδομάδα πριν από την προθεσμία.`,
        `Μετά την προθεσμία, δείτε ποιοι μένουν και επικοινωνήστε προσωπικά όπου χρειάζεται.`,
        `Ο τόνος μετράει.`,
        `Μια υπενθύμιση δεν χρειάζεται να ακούγεται σαν απαίτηση. Το ζητούμενο είναι να διευκολύνετε τον άλλον να σας πει ένα καθαρό ναι ή όχι.`,
      ],
    },
    {
      heading: "Τι κάνετε όταν κάποιος δεν απαντά",
      content: [
        `Μια προθεσμία που πέρασε δεν σημαίνει ούτε ότι ο καλεσμένος έρχεται ούτε ότι δεν έρχεται.`,
        `Χρειάζεστε καθαρή απάντηση.`,
        `Δείτε πρώτα μήπως απάντησε από άλλο κανάλι ή μήπως απάντησε κάποιος άλλος από το σπίτι του για λογαριασμό του.`,
        `Αν πάλι τίποτα, πάρτε τον τηλέφωνο.`,
        `Το μήνυμα είναι απλό:`,
        `Κλείνετε τον αριθμό και θα θέλατε να ξέρετε αν θα είναι μαζί σας.`,
        `Μόλις πάρετε απάντηση, καταγράψτε την — μην την αφήσετε στη μνήμη σας ή σε μια παλιά συνομιλία στο κινητό.`,
        `Αυτό γίνεται ιδιαίτερα σημαντικό μόλις αρχίσετε το πλάνο τραπεζιών.`,
      ],
    },
    {
      heading: "Ίδια προθεσμία για όλους;",
      content: [
        `Συνήθως ναι.`,
        `Μία προθεσμία λέγεται πιο εύκολα και παρακολουθείται πολύ πιο εύκολα.`,
        `Το χρονοδιάγραμμά σας μπορεί όμως να λάβει υπόψη τις διαφορετικές ανάγκες με άλλον τρόπο.`,
        `Μπορείτε, για παράδειγμα, να στείλετε τα προσκλητήρια νωρίτερα σε όλους, αν αρκετοί ταξιδεύουν.`,
        `Σπάνια χρειάζεται μία ημερομηνία για τους ντόπιους και άλλη για όσους έρχονται από αλλού, εκτός αν υπάρχει συγκεκριμένος πρακτικός λόγος.`,
        `Όσο πιο απλό το σύστημα, τόσο πιο εύκολα το κρατάτε.`,
      ],
    },
    {
      heading: "Ένα απλό χρονοδιάγραμμα",
      content: [
        `Ένα πρακτικό παράδειγμα για γάμο στην πόλη σας, με λίγο μεγαλύτερο περιθώριο από την αφετηρία που είπαμε παραπάνω.`,
        { type: "subheading", text: "8 έως 10 εβδομάδες πριν" },
        `Φεύγουν τα προσκλητήρια, ώστε να έχουν όλοι χρόνο να τα λάβουν και να απαντήσουν.`,
        { type: "subheading", text: "6 έως 8 εβδομάδες πριν" },
        `Οι καλεσμένοι έχουν το προσκλητήριο στα χέρια τους και αρχίζουν να οργανώνονται.`,
        { type: "subheading", text: "Περίπου 4 εβδομάδες πριν" },
        `Η προθεσμία RSVP.`,
        { type: "subheading", text: "Περίπου 3 εβδομάδες πριν" },
        `Υπενθύμιση σε όσους δεν έχουν απαντήσει.`,
        { type: "subheading", text: "Περίπου 2 εβδομάδες πριν" },
        `Δίνετε τον τελικό αριθμό στον χώρο και στο catering, σύμφωνα με τη δική τους προθεσμία.`,
        { type: "subheading", text: "Μετά τον τελικό αριθμό" },
        `Κλείνετε ό,τι έμεινε, με βάση τους επιβεβαιωμένους καλεσμένους, μαζί με το πλάνο τραπεζιών.`,
        `Αυτά είναι παραδείγματα, όχι κανόνες.`,
        `Αν ο χώρος ή το catering θέλει τον αριθμό νωρίτερα, πάει νωρίτερα και η προθεσμία.`,
      ],
    },
    {
      heading: "Η προθεσμία δεν είναι ο τελικός αριθμός",
      content: [
        `Η διάκριση ξεφεύγει εύκολα.`,
        `Η προθεσμία RSVP είναι η ημερομηνία μέχρι την οποία ζητάτε απαντήσεις.`,
        `Ο τελικός αριθμός είναι αυτό που δίνετε στους συνεργάτες σας, αφού δείτε τις απαντήσεις και τακτοποιήσετε ό,τι έμεινε ανοιχτό.`,
        `Δεν χρειάζεται να πέφτουν την ίδια μέρα.`,
        `Αυτό το κενό είναι χρήσιμο.`,
        `Σας δίνει χρόνο για:`,
        {
          type: "list",
          items: [
            `όσους δεν απάντησαν`,
            `τις ασαφείς απαντήσεις`,
            `τις αλλαγές της τελευταίας στιγμής`,
            `τις διορθώσεις σε παρέες και συνοδούς`,
            `έναν τελευταίο έλεγχο της λίστας`,
          ],
        },
        `Δείτε την προθεσμία ως την αρχή της διαδικασίας που κλείνει τον αριθμό, όχι ως το τέλος της.`,
      ],
    },
    {
      heading: "Από τις απαντήσεις στα υπόλοιπα",
      content: [
        `Οι απαντήσεις των καλεσμένων τροφοδοτούν πολλά ακόμη κομμάτια του γάμου.`,
        `Μόλις ξέρετε ποιοι έρχονται, μπορείτε να προχωρήσετε σε:`,
        {
          type: "list",
          items: [
            `τον τελικό αριθμό ατόμων`,
            `το ποιοι έρχονται στο μυστήριο και ποιοι στη δεξίωση`,
            `τις ιδιαιτερότητες στο φαγητό`,
            `το πλάνο τραπεζιών`,
            `τη χωρητικότητα των τραπεζιών`,
            `την ενημέρωση των συνεργατών σας`,
          ],
        },
        `Γι' αυτό αξίζει να κρατάτε τις απαντήσεις οργανωμένες, αντί να τις αφήνετε σκόρπιες σε μηνύματα και τηλεφωνήματα.`,
        `Και το πλάνο τραπεζιών ειδικά θέλει να στηρίζεται σε αυτούς που περιμένετε πραγματικά, όχι σε μια πρώτη εκδοχή της λίστας.`,
        {
          type: "p",
          text: [
            `Για το επόμενο βήμα, διαβάστε το `,
            {
              text: "Πλάνο τραπεζιών γάμου: πώς να το φτιάξετε βήμα βήμα",
              href: "/guides/wedding-seating-plan",
            },
            `.`,
          ],
        },
      ],
    },
    {
      heading: "Από την προθεσμία σε μια κλειστή λίστα",
      content: [
        {
          type: "note",
          text: `Τα εργαλεία RSVP στο Agapaé είναι διαθέσιμα στο πακέτο Heirloom.`,
        },
        `Στο Agapaé κρατάτε τους καλεσμένους που καλείτε και βλέπετε τις απαντήσεις τους πριν προχωρήσετε στα επόμενα στάδια της οργάνωσης.`,
        {
          type: "p",
          text: [
            `Για τη διαδικασία που μετατρέπει τους επιβεβαιωμένους σε πλάνο τραπεζιών, δείτε το `,
            {
              text: "Πλάνο τραπεζιών γάμου: πώς να το φτιάξετε βήμα βήμα",
              href: "/guides/wedding-seating-plan",
            },
            `. Για το ίδιο το προϊόν, δείτε το `,
            { text: "RSVP γάμου", href: "/wedding-rsvp" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Πότε πρέπει να απαντήσουν οι καλεσμένοι στον γάμο;`,
      a: `Στους περισσότερους γάμους μια πρακτική προθεσμία είναι τρεις με τέσσερις εβδομάδες πριν. Η τελική ημερομηνία βγαίνει από το πότε θέλουν τον αριθμό ο χώρος και το catering, αφήνοντας χρόνο για όσους δεν απάντησαν.`,
    },
    {
      q: `Τι σημαίνει RSVP στο προσκλητήριο;`,
      a: `Είναι η παράκληση να απαντήσει ο καλεσμένος αν θα έρθει. Για εσάς είναι κάτι παραπάνω: είναι ο τρόπος με τον οποίο φτάνετε σε έναν αριθμό που μπορείτε να δώσετε στους συνεργάτες σας και σε ένα πλάνο τραπεζιών που στέκει.`,
    },
    {
      q: `Φτάνουν οι τέσσερις εβδομάδες;`,
      a: `Για πολλούς γάμους ναι, και είναι μια συνηθισμένη αφετηρία. Όποιος όμως πρέπει να ταξιδέψει χρειάζεται περισσότερο χρόνο, και η προθεσμία σας πρέπει πάντα να προλαβαίνει τον τελικό αριθμό που ζητούν οι συνεργάτες σας.`,
    },
    {
      q: `Πόσο νωρίς στέλνονται τα προσκλητήρια;`,
      a: `Συνήθως έξι με οκτώ εβδομάδες πριν. Στείλτε τα νωρίτερα όταν οι καλεσμένοι πρέπει να κανονίσουν ταξίδι, διαμονή ή άδεια, ή όταν τα δίνετε στο χέρι και χρειάζεστε εσείς χρόνο.`,
    },
    {
      q: `Τι κάνω αν κάποιος δεν απαντήσει μέχρι την προθεσμία;`,
      a: `Επικοινωνήστε απευθείας και ζητήστε καθαρή απάντηση. Δείτε πρώτα μήπως απάντησε κάποιος άλλος από το σπίτι του. Μόλις τη λάβετε, καταγράψτε τη, ώστε ο τελικός αριθμός και το πλάνο τραπεζιών να στηρίζονται σε επιβεβαιωμένους καλεσμένους.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: ["how-to-organize-wedding-rsvps", "how-to-organize-wedding-guest-list"],

    product: [
      {
        href: "/wedding-rsvp",
        label: "RSVP γάμου",
        blurb:
          "Δείτε πώς δουλεύουν οι απαντήσεις στο Agapaé, από τους καλεσμένους που καλείτε μέχρι τις απαντήσεις που ελέγχετε πριν από τα υπόλοιπα. Διαθέσιμο στο πακέτο Heirloom.",
      },
    ],
  },
};

export default whenShouldWeddingGuestsRsvpEl;
